import { Icon, ICONS } from 'components/Icon';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { randomKey, resizeToWidthHeight } from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { LayoutChangeEvent, View } from 'react-native';
// eslint-disable-next-line import/no-named-as-default
import SignatureCanvas from 'react-signature-canvas';

type Props = {
  onChange: (data: string) => void;
};

const BaseWidth = 500;
const BaseHeight = BaseWidth / 2.5; // 200;

export const SignaturePad: React.FC<Props> = ({ onChange }) => {
  const signatureRef = React.useRef<SignatureCanvas>(null);
  const signatureKey = React.useRef<string>(randomKey());
  const forceUpdate = useForceUpdate();

  const wh = React.useRef<{
    width: number;
    height: number;
  }>({
    width: 0,
    height: 0,
  });

  const handleGetSvg = React.useCallback(() => {
    onChange(signatureRef.current?.toDataURL('image/svg+xml', {}) || '');
  }, [onChange, signatureRef]);

  // Reset canvas by updating signatureKey and reloading component
  const resetCanvas = React.useCallback(() => {
    signatureKey.current = randomKey();
    forceUpdate();
  }, [forceUpdate]);

  // Get a "good" width and height which fits inside screen and reset canvas
  const handleLayout = React.useCallback(
    (event: LayoutChangeEvent) => {
      const { width, height } = resizeToWidthHeight({
        width: BaseWidth,
        height: BaseHeight,
        outgoingWidth: event.nativeEvent.layout.width || BaseWidth,
        outgoingHeight: event.nativeEvent.layout.height || BaseHeight,
        allowScaleUp: false,
      });
      wh.current = {
        width: Math.round(width),
        height: Math.round(height),
      };
      resetCanvas();
    },
    [resetCanvas],
  );

  return (
    <View
      onLayout={handleLayout}
      style={{
        flex: 1,
        alignItems: 'center',
      }}
    >
      <View
        style={{
          width: wh.current.width,
          height: wh.current.height,
          backgroundColor: 'white',
        }}
      >
        <SignatureCanvas
          key={signatureKey.current}
          ref={signatureRef}
          onEnd={handleGetSvg}
          clearOnResize={false}
          canvasProps={{
            width: wh.current.width,
            height: wh.current.height,
          }}
        />
        <View
          style={{
            position: 'absolute',
            bottom: theme.paddingHorizontalHalf,
            right: theme.paddingHorizontal,
            pointerEvents: 'none',
          }}
        >
          <Icon
            size={36}
            source={ICONS.SignatureLight}
            color={theme.colors.placeholder}
          />
        </View>
      </View>
    </View>
  );
};
